import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/utils";

export type ButtonVariant = "default" | "ghost" | "outline" | "accent";
export type ButtonSize = "default" | "sm" | "lg" | "icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const base =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--primary)] " +
  "disabled:pointer-events-none disabled:opacity-50 select-none cursor-pointer";

const variants: Record<ButtonVariant, string> = {
  default:
    "bg-[color:var(--primary)] text-[color:var(--bg)] hover:opacity-90",
  accent:
    "bg-[color:var(--accent)] text-white hover:opacity-90",
  ghost:
    "bg-transparent text-[color:var(--text)] hover:bg-[color:var(--surface-2)]",
  outline:
    "border border-[color:var(--border)] bg-transparent text-[color:var(--text)] hover:bg-[color:var(--surface-2)]",
};

const sizes: Record<ButtonSize, string> = {
  default: "h-10 px-4 text-sm",
  sm: "h-8 px-3 text-xs",
  lg: "h-12 px-6 text-base",
  icon: "h-10 w-10 p-0",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", type = "button", ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(base, variants[variant], sizes[size], className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
